/**
 * useAutoplay.ts
 * يقدّم مؤشّر الوقت تلقائياً أثناء التشغيل، ويتوقّف ويعود للبداية عند النهاية.
 */

import { useEffect } from 'react';
import { useWeatherStore } from '../../../store/weatherStore';
import { setPlaybackPosition } from '../../../services/playbackClock';

interface Params {
    isPlaying: boolean;
    playbackSpeed: number;
    /** عدد إطارات الشريط الزمني (ساعات التوقّع) — مستقلّ عن Open-Meteo. */
    frameCount: number;
    /**
     * يُرجع true إذا كان إطار الوقت المطلوب جاهزاً (مخزّن) للعرض. إن أرجع false،
     * يتباطأ التشغيلُ (ولا يتجمّد) ويحفّز التحميل المُسبق — منطق Zoom Earth.
     */
    canAdvance?: (timeIndex: number) => boolean;
    /** الإطار الذي يعود إليه التشغيل عند بلوغ النهاية — إطار "الآن" لا بداية الدورة (الماضي). */
    homeIndex?: number;
    /**
     * دقّة **الكسر الذي يُنشر إلى متجر zustand** (لا دقّة الحركة).
     *
     * صارت الحركة مستمرّة تماماً عبر playbackClock (قيمة عشرية كل نبضة رسم)، فلم يعد
     * لهذا الرقم أي أثر على النعومة. بقي دوره الوحيد: تحديد كم مرّة يُعاد رسم React لأجل
     * الواجهة (تسميات الشريط، اشتقاق ساعة الرياح على محور القمر). ستّ درجات لكل إطار
     * تكفي الواجهة تماماً وتُبقي إعادة الرسم عند ~3/ثانية بدل 120.
     */
    subSteps?: number;
    /**
     * زمن عبور **إطار بيانات واحد** بالساعة الحقيقية (مللي ثانية، عند السرعة 1).
     *
     * هذا هو مقبض السرعة الحقيقي، وهو مستقلّ عن النعومة تماماً.
     */
    frameMs?: number;
    /** يُكرّر الحلقة من البداية عند النهاية بدل التوقّف (سلوك القمر الصناعي في Zoom Earth). */
    loop?: boolean;
}

/**
 * أدنى معامل سرعة أثناء انتظار وصول إطار (0 = تجمّد كامل).
 *
 * كان السلوك السابق توقّفاً صلباً: عند `canAdvance === false` يُصفَّر الرصيد ويتجمّد
 * المؤشّر حتى يصل الملفّ، ثم يقفز. وهذا بالضبط ما ظهر في قياس الإطارات كقفزات كبرى
 * (0.13 و0.18 و0.22 مقابل خطّ قاعدي 0.001). مشغّل الفيديو لا يتجمّد عند نفاد المخزون
 * بل يتباطأ، فيبقى الحسّ بالحركة قائماً. نُبقي 12% من السرعة: يزحف الكسر نحو نهاية
 * الإطار الحالي بلا أن يتجاوزه، فيُقرأ تباطؤاً لا تجمّداً.
 */
const STALL_SPEED = 0.12;

/** أقصى ما يقترب منه الكسر من الإطار التالي وهو غير جاهز — دونه بقليل كي لا نتجاوزه. */
const STALL_CEILING = 0.985;

/**
 * أقصى انتظار عند حدّ الإطار قبل التقدّم رغم عدم الجهوز (مضروباً في زمن الإطار).
 *
 * بلا هذا السقف يصير الانتظار **أبدياً**: أي إطار لا يصل (ملفّ ناقص في الدورة، خطأ 404،
 * شبكة تسقط الطلب) يُبقي `canAdvance` على false للأبد، فيزحف المؤشّر إلى STALL_CEILING
 * ويتجمّد هناك بينما التشغيل «يعمل» ظاهرياً. قِسناه حيّاً: `isPlaying = true` والكسر
 * مثبَّت عند 0.83 لا يتحرّك، فيبدو للمستخدم أن التشغيل لا يعمل أصلاً.
 *
 * الانتظار الصحيح محدود: نتباطأ لنمنح الملفّ فرصة، ثم نمضي. إطار مفقود يعني قفزة واحدة
 * صغيرة؛ الجمود يعني ميزة معطّلة بالكامل. مشغّلات الفيديو تفعل الشيء نفسه — تتخطّى
 * المقطع التالف بدل التوقّف عنده.
 *
 * 1.5× زمن الإطار: عند 3000 مللي للإطار = 4.5 ثانية انتظار، وهي أضعاف زمن تحميل ملفّ
 * 300 ك.ب على أي شبكة معقولة، فلا تُصرف إلا حين يكون الملفّ غير موجود فعلاً.
 */
const STALL_MAX_FRAMES = 1.5;

/** زمن الوقوف على آخر إطار قبل العودة إلى أوّل الحلقة (مللي). */
const LOOP_END_HOLD_MS = 1500;

export function useAutoplay({ isPlaying, playbackSpeed, frameCount, canAdvance, homeIndex = 0, subSteps = 6, frameMs = 3000, loop = false }: Params): void {
    useEffect(() => {
        if (!isPlaying || frameCount < 2) return;
        const length = frameCount;
        // دقّة النشر إلى المتجر (الواجهة فقط) — لا علاقة لها بنعومة الحركة.
        const SUB = Math.max(1, Math.round(subSteps));
        const perFrameMs = Math.max(1, frameMs / playbackSpeed);   // زمن عبور إطار كامل

        const store0 = useWeatherStore.getState();
        // نبدأ من موضع المتجر الحالي كي لا يقفز التشغيل عند الضغط على «تشغيل».
        let index = store0.currentTimeIndex;
        let fraction = store0.frameFraction;
        // موضعٌ خارج مدى الحلقة (المستخدم سحب المؤشّر إلى تنبّؤ الرادار ثم ضغط تشغيل):
        // تبدأ الحلقة من أوّلها بدل أن تسير في ما استُبعد منها.
        if (index >= length) { index = 0; fraction = 0; }
        let lastTs = performance.now();
        let rafId = 0;
        // آخر ما نُشر إلى المتجر — نتجنّب set متكرّر بنفس القيمة (إعادة رسم بلا سبب).
        let pubIndex = index;
        let pubSub = Math.round(fraction * SUB);
        // ذاكرة قصيرة لنتيجة canAdvance (راجع الاستدعاء في tick).
        let readyFor = -1, readyAt = 0, readyCache = true;
        // متى بدأ الانتظار الحالي عند حدّ الإطار (0 = لا انتظار) — راجع STALL_MAX_FRAMES.
        let stallSince = 0;

        /** ينشر الموضع: مستمرّاً إلى الـ GPU دائماً، ومكمَّماً إلى React عند الحاجة فقط. */
        const publish = () => {
            /*
             * على آخر إطار في الحلقة تقف الصورة ولا تمتزج نحو ما بعده. في الرادار ما بعده
             * **تنبّؤ** مستبعَد من الحلقة (طلب العميل 2026-09-24: «المؤشّر يوقف عند الرصد
             * الحالي ويرجع من جديد»)، والكسر هنا يعدّ زمن الوقفة لا غير.
             */
            const shown = loop && index === length - 1 ? 0 : fraction;
            setPlaybackPosition(index, shown);
            const sub = Math.min(SUB - 1, Math.floor(fraction * SUB));
            if (index !== pubIndex || sub !== pubSub) {
                pubIndex = index; pubSub = sub;
                useWeatherStore.getState().setTimelinePosition(index, sub / SUB);
            }
        };

        // نحسب التقدّم من **الزمن الحقيقي المنقضي** لا من عدّ النبضات، فيبقى الإيقاع دقيقاً
        // حتى لو تأخّرت النبضات (خيط مشغول/تبويب مخنوق). requestAnimationFrame بدل
        // setInterval: يتزامن مع دورة الرسم/التركيب الفعلية للمتصفح فتختفي رعشة التوقيت.
        //
        // لا «خطوات» ولا رصيد متراكم بعد اليوم: الكسر عدد عشري يتقدّم بمقدار (dt / زمن
        // الإطار) في كل نبضة، فالحركة مستمرّة بدقّة الشاشة نفسها. وأي تعثّر لحظي يظهر
        // كتباطؤ سلس لا كقفزة، لأننا نقصّ dt بحدّ أعلى بدل تعويضه دفعةً واحدة.
        const tick = () => {
            rafId = requestAnimationFrame(tick);
            const now = performance.now();
            // قصّ الفجوة: تبويب عاد من الخلفية أو تعثّر طويل لا يجوز أن يُترجَم قفزةً.
            const dt = Math.min(now - lastTs, perFrameMs);
            lastTs = now;

            /*
             * ── وقفةٌ على آخر إطار قبل العودة ────────────────────────────────────
             * في الحلقة كان آخر إطار (نهاية تنبّؤ الرادار) يُغادَر إلى أوّل الشريط —
             * قبل ساعتين — بلا توقّف، فيبدو المطر كأنه ارتدّ إلى الخلف فجأةً (فيديو
             * المستخدم 2026-09-22). Windy وZoom Earth يقفان على النهاية لحظةً ثم يعودان،
             * فيُقرأ الانتقال «بدايةً جديدة» لا ارتداداً.
             */
            const hold = loop && index === length - 1 ? LOOP_END_HOLD_MS : 0;
            let advance = dt / (perFrameMs + hold);      // نسبة من إطار كامل

            // هل الإطار التالي جاهز؟ إن لا، نتباطأ ونحفّز تحميله (canAdvance يفعل ذلك).
            //
            // نفحص كل 120 مللي لا كل نبضة: صار tick يعمل 60 مرّة في الثانية (كان يعمل عند
            // حدود الخطوات فقط)، و`canAdvance` يبني روابط نصّية ويستدعي التحميل المسبق —
            // فستّون فحصاً في الثانية عملٌ مهدور على الخيط الرئيسي بلا أي فائدة، إذ لا
            // تتغيّر جهوزيّة ملفّ خلال 16 مللي. والقرار يُعاد تقييمه فوراً عند تغيّر الفهرس.
            const next = index + 1 >= length ? (loop ? 0 : index) : index + 1;
            if (canAdvance && (next !== readyFor || now - readyAt > 120)) {
                readyFor = next;
                readyAt = now;
                readyCache = canAdvance(next);
            }
            let ready = !canAdvance || readyCache;

            // ساعة الانتظار: تبدأ عند أوّل نبضة غير جاهزة وتُصفَّر فور الجهوز.
            if (ready) {
                stallSince = 0;
            } else {
                if (stallSince === 0) stallSince = now;
                // نفد الصبر: امضِ رغم عدم الجهوز بدل التجمّد عند حدّ الإطار إلى الأبد.
                if (now - stallSince > perFrameMs * STALL_MAX_FRAMES) {
                    ready = true;
                    stallSince = 0;
                }
            }
            if (!ready) advance *= STALL_SPEED;

            fraction += advance;

            while (fraction >= 1) {
                if (!ready) {                     // لا نعبر إلى إطار لم يجهز — نزحف تحته
                    fraction = STALL_CEILING;
                    break;
                }
                const nextIndex = index + 1;
                if (nextIndex >= length) {
                    if (loop) { index = 0; fraction -= 1; continue; }
                    // النهاية → عُد إلى "الآن" وتوقّف.
                    index = Math.max(0, Math.min(length - 1, homeIndex));
                    fraction = 0;
                    setPlaybackPosition(index, 0);
                    const s = useWeatherStore.getState();
                    s.setIsPlaying(false);
                    s.setTimelinePosition(index, 0);
                    return;
                }
                index = nextIndex;
                fraction -= 1;
            }

            publish();
        };

        setPlaybackPosition(index, fraction);
        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    }, [isPlaying, playbackSpeed, frameCount, canAdvance, homeIndex, subSteps, frameMs, loop]);
}
